import React, { useState } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DigitalRiskChartProps {
  risk: number;
}

const DigitalRiskChart: React.FC<DigitalRiskChartProps> = ({ risk }) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  const riskValue = Math.ceil(risk);

  let criticidad = '';
  if (riskValue >= 80) criticidad = t('digitalRisk.critical');
  else if (riskValue >= 60) criticidad = t('digitalRisk.high');
  else if (riskValue >= 40) criticidad = t('digitalRisk.medium');
  else if (riskValue >= 20) criticidad = t('digitalRisk.low');
  else criticidad = t('digitalRisk.veryLow');

  // Dividimos el gráfico en dos partes: la parte de riesgo y la parte gris
  const data = [
    { name: t('digitalRisk.risk'), value: riskValue, color: getRiskColor(riskValue) },
    { name: t('digitalRisk.remaining'), value: 100 - riskValue, color: '#d3d3d3' },
  ];

  const COLORS = data.map((d) => d.color);

  return (
    <div className="mt-8 text-center flex flex-col items-center relative">
      {/* Título con el signo de interrogación */}
      <div className="relative inline-block mb-2">
        <h3 className="text-2xl font-bold inline-flex items-center">
          {t('digitalRisk.title')}
          <span
            className="ml-2 inline-flex items-center text-gray-500 cursor-pointer relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
          >
            <HelpCircle className="w-5 h-5" />
            {showTooltip && (
              <div className="absolute left-0 mt-2 w-64 p-3 bg-gray-700 text-white rounded-lg shadow-lg z-10 transition-opacity duration-200 opacity-100 text-sm">
                {t('digitalRisk.tooltip')}
              </div>
            )}
          </span>
        </h3>
      </div>

      {/* Gráfico de niveles de riesgo */}
      <div className="flex justify-center relative" style={{ height: '150px' }}>
        <PieChart width={200} height={180}>
          <Pie
            data={data}
            startAngle={180}
            endAngle={0}
            innerRadius={60}
            outerRadius={80}
            paddingAngle={5}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
      </div>

      {/* Texto de riesgo */}
      <p className="text-xl font-semibold mt-0">
        {riskValue} - {criticidad}
      </p>
    </div>
  );
};

// Función para obtener el color según el valor del riesgo
const getRiskColor = (riskValue: number): string => {
  if (riskValue >= 80) return 'rgba(255, 35, 38, 1)'; // Rojo suavizado (Crítico)
  if (riskValue >= 60) return 'rgba(255, 130, 51, 1)'; // Naranja suavizado (Alto)
  if (riskValue >= 40) return 'rgba(253, 203, 73, 1)'; // Amarillo suavizado (Medio)
  if (riskValue >= 20) return 'rgba(173, 255, 47, 1)'; // Amarillo-verde suavizado (Bajo)
  if (riskValue < 20) return 'rgba(0, 255, 0, 1)';       // Verde suavizado
  return 'rgba(211, 211, 211, 0.9)'; // Gris claro para el restante
};

export default DigitalRiskChart;
